import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import api from '../../services/api';
import RepresentationWhitelist from '../admin/service-groups/RepresentationWhitelist';

/**
 * Therapist-side client defaults editor (Meeting 3 implementation, T2-2a).
 *
 * Therapist sets defaults that apply to all of their assigned clients. The
 * therapist cannot weaken regulatory floors:
 *   - Cannot turn off the per-bubble AI label.
 *   - Cannot disable the chat disclaimer footer (only edit copy).
 *   - Cannot widen the voice catalog whitelist beyond the service-group set.
 *   - Cannot pick photo_real avatars.
 *   - "Replacement for human therapy" wording is rejected at every layer.
 */
const ROLE_LABEL_OPTIONS = [
  'AI Assistant',
  'AI Therapist',
  'AI Coach',
  'AI Supporter',
  'AI Companion',
  'AI Guide'
];
const REJECTED_DISCLAIMER = /replacement\s+for\s+human\s+therapy/i;

const HEX_RE = /^#?[0-9A-Fa-f]{6}$/;

function relativeLuminance(r, g, b) {
  const srgb = [r, g, b].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function contrastRatio(hex1, hex2) {
  const parse = (h) => {
    const m = h.replace('#', '');
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
  };
  if (!HEX_RE.test(hex1) || !HEX_RE.test(hex2)) return null;
  const [r1, g1, b1] = parse(hex1);
  const [r2, g2, b2] = parse(hex2);
  const L1 = relativeLuminance(r1, g1, b1);
  const L2 = relativeLuminance(r2, g2, b2);
  const [hi, lo] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (hi + 0.05) / (lo + 0.05);
}

const ClientDefaultsEditor = () => {
  const [enabled, setEnabled] = useState(false);
  const [ui, setUi] = useState({});
  const [va, setVa] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/therapist/me/client-defaults')
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setEnabled(d.overrideEnabled === true);
        setUi(d.clientUi || {});
        setVa(d.voiceAndAvatar || {});
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const themeBg = ui.theme?.background || '#ffffff';
  const themePrimary = ui.theme?.primary || '#1976d2';
  const contrast = contrastRatio(themeBg, themePrimary);
  const contrastOk = contrast === null || contrast >= 4.5;

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (ui.chatDisclaimerFooter?.text && REJECTED_DISCLAIMER.test(ui.chatDisclaimerFooter.text)) {
      setError('Footer text cannot use "replacement for human therapy" wording.');
      return;
    }
    if (ui.theme && Object.values(ui.theme).some((v) => v && !HEX_RE.test(v))) {
      setError('Theme colours must be 6-digit hex like #1976d2.');
      return;
    }
    if (!contrastOk) {
      setError('Theme palette fails WCAG-AA 4.5:1 contrast for primary on background. Adjust before saving.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        overrideEnabled: enabled,
        clientUi: ui,
        voiceAndAvatar: va
      };
      const res = await api.put('/therapist/me/client-defaults', payload);
      setUi(res.data.clientUi || ui);
      setVa(res.data.voiceAndAvatar || va);
      setSuccess('Client defaults saved. Your assigned clients will pick these up on next refresh.');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aimhi:clientUiSettingsChanged'));
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" gutterBottom>
        Client experience defaults
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        These defaults apply to all clients assigned to you. They sit on top of the platform default
        and your service group's overrides — and can themselves be further specialised per client.
        Floors that always apply: under-18 service groups always show the AI label on every bubble;
        the disclaimer footer cannot be turned off here; "replacement for human therapy" wording is
        rejected; <code>photo_real</code> avatars are not available at this layer.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        }
        label="Apply my defaults to all my clients"
        sx={{ display: 'block', mb: 2 }}
      />

      <Divider sx={{ my: 2 }}>Chat experience</Divider>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="td-role-label">Role label override</InputLabel>
        <Select
          labelId="td-role-label"
          label="Role label override"
          value={ui.defaultRoleLabel || ''}
          onChange={(e) => setUi({ ...ui, defaultRoleLabel: e.target.value || null })}
        >
          <MenuItem value=""><em>Inherit from service group / platform</em></MenuItem>
          {ROLE_LABEL_OPTIONS.map((l) => (
            <MenuItem key={l} value={l}>{l}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="td-bubble-style">Per-bubble label style</InputLabel>
        <Select
          labelId="td-bubble-style"
          label="Per-bubble label style"
          value={ui.perBubbleAILabelStyle || ''}
          onChange={(e) => setUi({ ...ui, perBubbleAILabelStyle: e.target.value || null })}
        >
          <MenuItem value=""><em>Inherit</em></MenuItem>
          <MenuItem value="full">Full</MenuItem>
          <MenuItem value="subtle">Subtle (corner tag)</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="td-avatar-display">AI avatar display</InputLabel>
        <Select
          labelId="td-avatar-display"
          label="AI avatar display"
          value={ui.aiAvatarDisplay || ''}
          onChange={(e) => setUi({ ...ui, aiAvatarDisplay: e.target.value || null })}
        >
          <MenuItem value=""><em>Inherit</em></MenuItem>
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="header">Header only</MenuItem>
          <MenuItem value="grouped">Grouped (first message of an AI run)</MenuItem>
          <MenuItem value="every_message">Every AI message</MenuItem>
        </Select>
      </FormControl>

      <RepresentationWhitelist
        value={ui.allowedRepresentationKeys || []}
        onChange={(next) => setUi({ ...ui, allowedRepresentationKeys: next })}
        helperText="Empty = inherit from your service group / platform. Pick a subset to further narrow what your clients see."
      />


      <TextField
        fullWidth
        multiline
        rows={2}
        label="Chat footer text override"
        value={ui.chatDisclaimerFooter?.text || ''}
        onChange={(e) => setUi({
          ...ui,
          chatDisclaimerFooter: { ...(ui.chatDisclaimerFooter || {}), text: e.target.value || null }
        })}
        helperText='Cannot contain "replacement for human therapy". Leave blank to inherit.'
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={ui.showNavEmojis === true}
            onChange={(e) => setUi({ ...ui, showNavEmojis: e.target.checked })}
          />
        }
        label="Show emoji prefixes on nav for my clients"
        sx={{ display: 'block', mb: 2 }}
      />

      <Divider sx={{ my: 2 }}>Theme palette</Divider>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 1 }}>
        <TextField
          label="Primary (#hex)"
          value={ui.theme?.primary || ''}
          onChange={(e) => setUi({ ...ui, theme: { ...(ui.theme || {}), primary: e.target.value || null } })}
        />
        <TextField
          label="Secondary (#hex)"
          value={ui.theme?.secondary || ''}
          onChange={(e) => setUi({ ...ui, theme: { ...(ui.theme || {}), secondary: e.target.value || null } })}
        />
        <TextField
          label="Accent (#hex)"
          value={ui.theme?.accent || ''}
          onChange={(e) => setUi({ ...ui, theme: { ...(ui.theme || {}), accent: e.target.value || null } })}
        />
        <TextField
          label="Background (#hex)"
          value={ui.theme?.background || ''}
          onChange={(e) => setUi({ ...ui, theme: { ...(ui.theme || {}), background: e.target.value || null } })}
        />
      </Box>
      {contrast !== null && (
        <Alert severity={contrastOk ? 'success' : 'warning'} sx={{ mb: 2 }}>
          Primary-on-background contrast: {contrast.toFixed(2)}:1 — {contrastOk ? 'passes WCAG-AA (4.5:1)' : 'fails WCAG-AA (must be ≥4.5:1)'}
        </Alert>
      )}

      <Divider sx={{ my: 2 }}>Avatar default</Divider>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="td-avatar-default">Default avatar style</InputLabel>
        <Select
          labelId="td-avatar-default"
          label="Default avatar style"
          value={va.avatar?.defaultStyle || ''}
          onChange={(e) => setVa({
            ...va,
            avatar: { ...(va.avatar || {}), defaultStyle: e.target.value || null }
          })}
        >
          <MenuItem value=""><em>Inherit</em></MenuItem>
          <MenuItem value="abstract">Abstract</MenuItem>
          <MenuItem value="illustrated">Illustrated</MenuItem>
          <MenuItem value="semi_realistic">Semi-realistic</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save defaults'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ClientDefaultsEditor;
